import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import apiClient from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const FeesContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.375rem;
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const SearchSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
`;

const SearchRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? '#000000' : '#dee2e6'};
  background: ${props => props.active ? '#000000' : 'white'};
  color: ${props => props.active ? 'white' : '#6c757d'};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: #000000;
    background: ${props => props.active ? '#000000' : '#f8f9fa'};
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const FeesList = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 12px;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
    
    &:hover {
      background: #555;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
`;

const Thead = styled.thead`
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
`;

const Th = styled.th`
  padding: 0.5rem 0.25rem;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;
  min-width: 32px;
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.125rem;
    font-size: 0.625rem;
    min-width: 24px;
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 0.5rem 0.25rem;
  font-size: 0.75rem;
  color: #495057;
  white-space: nowrap;
  text-align: center;
  min-width: 32px;
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.125rem;
    font-size: 0.625rem;
    min-width: 24px;
  }
`;

const NameCell = styled.td`
  padding: 0.5rem 0.5rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #000000;
  white-space: nowrap;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:hover {
    background: #f8f9fa;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.375rem;
    font-size: 0.75rem;
    max-width: 100px;
  }
`;

const PaymentIcon = styled.span<{ paid?: boolean; beforeRegistration?: boolean; dormant?: boolean; exempted?: boolean }>`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${props => {
    if (props.exempted) return '#6c757d';
    if (props.beforeRegistration) return '#adb5bd';
    if (props.dormant) return '#6c757d';
    return props.paid ? '#0f5132' : '#dc3545';
  }};
  display: inline-block;
  width: 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    width: 16px;
    height: 16px;
    line-height: 16px;
  }
`;

const Legend = styled.div`
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-size: 0.8125rem;
  color: #495057;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.5rem 0.75rem;
    gap: 0.5rem;
  }
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MonthSelector = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const MonthButton = styled.button<{ selected?: boolean; paid?: boolean }>`
  padding: 0.625rem 0.875rem;
  border-radius: 8px;
  border: 2px solid ${props => {
    if (props.selected) return '#000000';
    if (props.paid) return '#d1e7dd';
    return '#dee2e6';
  }};
  background: ${props => {
    if (props.selected) return '#000000';
    if (props.paid) return '#d1e7dd';
    return 'white';
  }};
  color: ${props => {
    if (props.selected) return 'white';
    if (props.paid) return '#0f5132';
    return '#6c757d';
  }};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #000000;
  }
`;

const MonthFormContainer = styled.div`
  padding: 1.5rem;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  background: #f8f9fa;
`;

const MonthFormTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MonthForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const YearSelect = styled.select`
  padding: 0.625rem 1rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  background: white;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #000000;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #000000;
  }
  
  &:focus {
    outline: none;
    border-color: #000000;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }
`;

interface PaymentDetail {
  amount: string;
  paymentDate: string;
  memo: string;
  payment_no?: number; // 납부 정보 번호 (수정/삭제용)
}

const isPaymentDetail = (payment: PaymentDetail | null | 'dormant'): payment is PaymentDetail => {
  return !!payment && payment !== 'dormant';
};

interface Member {
  id: number;
  name: string;
  status: string;
  phone?: string;  // 전화번호 추가
  registeredDate?: string;
  hasJoiningFee?: boolean;  // memo에 "가입비" 포함 여부
  joiningFeeAmount?: number;  // 가입비 금액
  hasUniformV1?: boolean;  // 유니폼 v1 보유 여부
  payments: {
    [month: string]: PaymentDetail | null | 'dormant';
  };
}

interface ExcelUploadSummary {
  upload_info: {
    upload_no: number;
    filename: string;
    uploaded_at: string;
    status: string;
    total_rows: number;
    registered_count: number;
  };
  monthly_summary: Array<{
    year: number;
    month: number;
    total_count: number;
    matched_count: number;
    unmatched_count: number;
    registered_count: number;
    already_registered_count?: number;
  }>;
}

interface ExcelDetail {
  detail_no: number;
  detail_nos?: number[];  // 합쳐진 경우 여러 detail_no
  row_number: number;
  row_numbers?: number[];  // 합쳐진 경우 여러 row_number
  transaction_date: string;
  transaction_dates?: string[];  // 합쳐진 경우 여러 거래일시
  category: string;
  amount: number;
  amounts?: number[];  // 합쳐진 경우 개별 금액 리스트
  balance: number;
  transaction_type: string;
  content: string;
  contents?: string[];  // 합쳐진 경우 여러 내용
  payment_month: number;
  payment_year: number;
  matched_user_no: number | null;
  matched_name: string | null;
  status: string;
  is_grouped?: boolean;  // 합쳐진 데이터인지 여부
  count?: number;  // 합쳐진 건수
  registered_date?: string | null;  // 회원 가입일
  registered_year?: number | null;  // 회원 가입 연도
  registered_month?: number | null;  // 회원 가입 월
}

const AdminFees: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('전체');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('1');
  const [editingMonth, setEditingMonth] = useState<{ [month: string]: PaymentDetail }>({});
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const currentYear = new Date().getFullYear();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [totalRows, setTotalRows] = useState(0);
  
  // 엑셀 업로드 관련 상태
  const [currentUploadNo, setCurrentUploadNo] = useState<number | null>(null);
  const [uploadSummary, setUploadSummary] = useState<ExcelUploadSummary | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedViewMonth, setSelectedViewMonth] = useState<number | null>(null);
  const [monthDetails, setMonthDetails] = useState<ExcelDetail[]>([]);
  const [monthDetailsLoading, setMonthDetailsLoading] = useState(false);
  const [allMembers, setAllMembers] = useState<Array<{id: number, name: string}>>([]);
  const [isExcelMappingModalOpen, setIsExcelMappingModalOpen] = useState(false);
  const [paymentMemos, setPaymentMemos] = useState<{ [key: number]: string }>({});  // detail_no별 메모
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [selectedDetailForRegister, setSelectedDetailForRegister] = useState<ExcelDetail | null>(null);
  const [selectedMonthsForRegister, setSelectedMonthsForRegister] = useState<Array<{year: number, month: number}>>([]);  // 선택된 달들
  const [existingPayments, setExistingPayments] = useState<Array<{year: number, month: number}>>([]);  // 이미 등록된 회비 목록
  const [viewMode, setViewMode] = useState<'normal' | 'test'>('normal');  // 일반 보기 / 테스트 보기
  const [memberPhoneMap, setMemberPhoneMap] = useState<{ [user_no: number]: string }>({});  // 회원별 전화번호 매핑

  // 회비 현황 조회
  const fetchFeeStatus = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/fees/status/${selectedYear}`);
      if (response.data.success) {
        // 백엔드 데이터를 프론트엔드 형식으로 변환
        const feeData = response.data.data.map((member: any) => {
          const payments: { [month: string]: PaymentDetail | null | 'dormant' } = {};
          for (let i = 1; i <= 12; i++) {
            const monthStatus = member[`month_${i}`];
            if (monthStatus === 'dormant') {
              payments[i.toString()] = 'dormant';
            } else if (monthStatus === true) {
              payments[i.toString()] = { amount: '20000', paymentDate: '', memo: '' };
            } else {
              payments[i.toString()] = null;
            }
          }
          return {
            id: member.user_no,
            name: member.name,
            status: '회원',
            phone: member.phone || '',  // 전화번호 추가
            registeredDate: member.registered_date,
            hasJoiningFee: member.has_joining_fee || false,
            joiningFeeAmount: member.joining_fee_amount || 0,
            hasUniformV1: member.has_uniform_v1 || false,
            payments
          };
        });
        setMembers(feeData);
      }
    } catch (error) {
      console.error('회비 현황 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeStatus();
    fetchAllMembers();
    fetchMemberPhones();
  }, [selectedYear]);

  // 회원 전화번호 조회 (SMS 전송용)
  const fetchMemberPhones = async () => {
    try {
      const response = await apiClient.get('/members/active');
      if (response.data.success) {
        const phoneMap: { [user_no: number]: string } = {};
        response.data.data.forEach((m: any) => {
          // 회원 목록 API는 id 필드로 user_no를 반환합니다
          const userNo = m.id || m.user_no;
          if (userNo && m.phone) {
            phoneMap[userNo] = m.phone;
          }
        });
        setMemberPhoneMap(phoneMap);
      }
    } catch (error) {
      console.error('회원 전화번호 조회 실패:', error);
    }
  };

  // 전체 회원 목록 조회 (매핑용)
  const fetchAllMembers = async () => {
    try {
      const response = await apiClient.get('/members/active');
      if (response.data.success) {
        setAllMembers(response.data.data.map((m: any) => ({
          id: m.user_no,
          name: m.name
        })));
      }
    } catch (error) {
      console.error('회원 목록 조회 실패:', error);
    }
  };

  // 엑셀 업로드 요약 조회
  const fetchUploadSummary = async (uploadNo: number) => {
    try {
      const response = await apiClient.get(`/fees/excel-upload/${uploadNo}/summary`);
      if (response.data.success) {
        setUploadSummary(response.data.data);
      }
    } catch (error) {
      console.error('업로드 요약 조회 실패:', error);
    }
  };

  // 월별 상세 데이터 조회
  const fetchMonthDetails = async (uploadNo: number, year: number, month: number) => {
    try {
      setMonthDetailsLoading(true);
      const response = await apiClient.get(`/fees/excel-upload/${uploadNo}/month/${year}/${month}`);
      if (response.data.success) {
        setMonthDetails(response.data.data);
      }
    } catch (error) {
      console.error('월별 데이터 조회 실패:', error);
      setMonthDetails([]);
    } finally {
      setMonthDetailsLoading(false);
    }
  };

  // 월 선택 핸들러
  const handleMonthSelect = (year: number, month: number) => {
    setSelectedYear(year);
    setSelectedViewMonth(month);
    if (currentUploadNo) {
      fetchMonthDetails(currentUploadNo, year, month);
      setIsExcelMappingModalOpen(true);
    }
  };

  // 매핑 업데이트
  const handleMappingUpdate = async (detailNo: number, userId: number) => {
    try {
      const response = await apiClient.put(`/fees/excel-detail/${detailNo}/mapping`, {
        user_no: userId
      });
      if (response.data.success) {
        // 목록 새로고침
        if (currentUploadNo && selectedViewMonth) {
          fetchMonthDetails(currentUploadNo, selectedYear, selectedViewMonth);
          fetchUploadSummary(currentUploadNo);
        }
      }
    } catch (error: any) {
      console.error('매핑 업데이트 실패:', error);
      window.alert(error.response?.data?.detail || '매핑 업데이트에 실패했습니다');
    }
  };

  // 등록 모달 열기
  const handleOpenRegisterModal = async (detail: ExcelDetail) => {
    setSelectedDetailForRegister(detail);
    setSelectedMonthsForRegister([]);
    
    // 이미 등록된 회비 목록 조회
    if (detail.matched_user_no) {
      try {
        const response = await apiClient.get(`/fees/user/${detail.matched_user_no}/${detail.payment_year}`);
        if (response.data.success) {
          const payments = response.data.data;
          const existing = payments.map((p: any) => ({
            year: detail.payment_year,
            month: p.month
          }));
          setExistingPayments(existing);
        } else {
          setExistingPayments([]);
        }
      } catch (error) {
        console.error('이미 등록된 회비 조회 실패:', error);
        setExistingPayments([]);
      }
    } else {
      setExistingPayments([]);
    }
    
    setIsRegisterModalOpen(true);
  };

  // 등록 모달 닫기
  const handleCloseRegisterModal = () => {
    setIsRegisterModalOpen(false);
    setSelectedDetailForRegister(null);
    setSelectedMonthsForRegister([]);
    setExistingPayments([]);
  };

  // 선택한 항목들 등록
  const handleRegisterPayments = async (detail: ExcelDetail, skipConfirm: boolean = false) => {
    const detailNos = detail.is_grouped ? (detail.detail_nos || [detail.detail_no]) : [detail.detail_no];
    const memo = paymentMemos[detail.detail_no] || '';

    if (detailNos.length === 0) {
      window.alert('등록할 항목을 선택해주세요');
      return;
    }

    // 가입월 확인 및 다중 월 선택 로직
    const registeredMonth = detail.registered_month;
    const registeredYear = detail.registered_year;
    const paymentMonth = detail.payment_month;
    const paymentYear = detail.payment_year;
    const amount = detail.amount;
    
    // 가입월이 있고, 입금 월이 가입월이 아니고, 금액이 2만원 초과인 경우
    const shouldShowMonthSelection = registeredMonth && 
                                      registeredYear &&
                                      !(paymentYear === registeredYear && paymentMonth === registeredMonth) &&
                                      amount > 20000;
    
    if (shouldShowMonthSelection && !skipConfirm) {
      // 모달 열기
      handleOpenRegisterModal(detail);
      return;
    }

    // skipConfirm이 false일 때만 확인
    if (!skipConfirm && !window.confirm(`선택한 ${detail.is_grouped ? `${detail.count}건 (합계: ${detail.amount.toLocaleString()}원)` : '1건'}의 회비를 등록하시겠습니까?`)) {
      return;
    }

    await executeRegisterPayments(detailNos, memo, detail, []);
  };

  // 실제 등록 실행
  const executeRegisterPayments = async (detailNos: number[], memo: string, detail: ExcelDetail, selectedMonths: Array<{year: number, month: number}>) => {
    try {

      const response = await apiClient.post('/fees/excel-register', {
        detail_nos: detailNos,
        memo: memo || undefined,
        selected_months: selectedMonths.length > 0 ? selectedMonths : undefined
      });
      
      if (response.data.success) {
        const result = response.data.data;
        window.alert(`총 ${result.total_registered}건의 회비가 등록되었습니다.`);
        
        // 메모 초기화
        setPaymentMemos(prev => {
          const newMemos = { ...prev };
          detailNos.forEach(no => delete newMemos[no]);
          return newMemos;
        });
        
        // 모달 닫기
        handleCloseRegisterModal();
        
        // 목록 새로고침
        if (currentUploadNo && selectedViewMonth) {
          fetchMonthDetails(currentUploadNo, selectedYear, selectedViewMonth);
          fetchUploadSummary(currentUploadNo);
        }
        fetchFeeStatus();
      }
    } catch (error: any) {
      console.error('회비 등록 실패:', error);
      window.alert(error.response?.data?.detail || '회비 등록에 실패했습니다');
    }
  };

  // 등록 모달에서 등록 실행
  const handleConfirmRegister = async () => {
    if (!selectedDetailForRegister) return;
    
    const detailNos = selectedDetailForRegister.is_grouped ? 
      (selectedDetailForRegister.detail_nos || [selectedDetailForRegister.detail_no]) : 
      [selectedDetailForRegister.detail_no];
    const memo = paymentMemos[selectedDetailForRegister.detail_no] || '';
    
    if (selectedMonthsForRegister.length === 0) {
      window.alert('등록할 달을 선택해주세요');
      return;
    }
    
    await executeRegisterPayments(detailNos, memo, selectedDetailForRegister, selectedMonthsForRegister);
  };

  const months = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  // 필터링된 회원 목록 (관리자 제외)
  const filteredMembers = members.filter(member => {
    const isRegularMember = member.status === '회원';
    const matchesSearch = member.name.includes(searchTerm);
    
    // 납부 상태 필터링 (현재 월 기준)
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const currentMonthStr = currentMonth.toString();
    const paymentStatus = member.payments[currentMonthStr];
    
    // 가입일 기준으로 가입 전 달 계산
    const getIsBeforeRegistration = (month: number): boolean => {
      if (!member.registeredDate) return false;
      const registeredDate = new Date(member.registeredDate);
      const registeredYear = registeredDate.getFullYear();
      const registeredMonth = registeredDate.getMonth() + 1; // 1-12
      
      // 현재 연도와 가입 연도가 다르면 가입 연도 기준으로 판단
      if (selectedYear !== registeredYear) {
        return selectedYear < registeredYear || (selectedYear === registeredYear && month < registeredMonth);
      }
      
      // 같은 연도면 가입 월 이전 달은 가입 전
      return month < registeredMonth;
    };
    
    // 가입월 회비 면제 여부 확인 (가입일이 25일~31일 사이)
    const getIsRegistrationMonthExempted = (month: number): boolean => {
      if (!member.registeredDate) return false;
      const registeredDate = new Date(member.registeredDate);
      const registeredYear = registeredDate.getFullYear();
      const registeredMonth = registeredDate.getMonth() + 1; // 1-12
      const registeredDay = registeredDate.getDate(); // 1-31
      
      // 가입월이 아니면 면제 아님
      if (selectedYear !== registeredYear || month !== registeredMonth) {
        return false;
      }
      
      // 가입일이 25일~31일 사이면 면제
      return registeredDay >= 25 && registeredDay <= 31;
    };
    
    const isBeforeRegistration = getIsBeforeRegistration(currentMonth);
    const isRegistrationMonthExempted = getIsRegistrationMonthExempted(currentMonth);
    const isDormant = paymentStatus === 'dormant';
    const isPaid = paymentStatus !== null && paymentStatus !== 'dormant';
    
    // 납부로 간주되는 경우: 실제 납부, 휴면, 가입 전, 가입월 면제
    const isConsideredPaid = isPaid || isDormant || isBeforeRegistration || isRegistrationMonthExempted;
    
    const matchesPaymentFilter = filterStatus === '전체' || 
                                (filterStatus === '납부' && isConsideredPaid) ||
                                (filterStatus === '미납' && !isConsideredPaid);
    
    // 테스트 모드일 때는 2025년 7월 31일 이후 가입자 중 memo에 "가입비"가 포함된 회원 또는 유니폼 v1이 없는 회원 필터링
    if (viewMode === 'test') {
      if (!member.registeredDate) return false;
      
      const registeredDate = new Date(member.registeredDate);
      const cutoffDate = new Date('2025-07-31');
      
      // 2025년 7월 31일 이후 가입자인지 확인 (7월 31일 당일 제외, 8월 1일부터)
      const isAfterCutoff = registeredDate > cutoffDate;
      
      // 조건: (가입비 포함 회원) 또는 (유니폼 v1 없는 회원)
      const hasJoiningFeeCondition = member.hasJoiningFee === true && isAfterCutoff;
      const hasNoUniformV1Condition = !member.hasUniformV1;
      
      return isRegularMember && matchesSearch && matchesPaymentFilter && (hasJoiningFeeCondition || hasNoUniformV1Condition);
    }
    
    return isRegularMember && matchesSearch && matchesPaymentFilter;
  });

  // 통계 계산 (일반 회원만)
  const regularMembers = members.filter(m => m.status === '회원');
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const currentMonthStr = currentMonth.toString();
  
  // 가입일 기준으로 가입 전 달 계산 헬퍼 함수
  const getIsBeforeRegistrationForStats = (member: Member, month: number): boolean => {
    if (!member.registeredDate) return false;
    const registeredDate = new Date(member.registeredDate);
    const registeredYear = registeredDate.getFullYear();
    const registeredMonth = registeredDate.getMonth() + 1; // 1-12
    
    if (selectedYear !== registeredYear) {
      return selectedYear < registeredYear || (selectedYear === registeredYear && month < registeredMonth);
    }
    
    return month < registeredMonth;
  };
  
  // 가입월 회비 면제 여부 확인 헬퍼 함수
  const getIsRegistrationMonthExemptedForStats = (member: Member, month: number): boolean => {
    if (!member.registeredDate) return false;
    const registeredDate = new Date(member.registeredDate);
    const registeredYear = registeredDate.getFullYear();
    const registeredMonth = registeredDate.getMonth() + 1; // 1-12
    const registeredDay = registeredDate.getDate(); // 1-31
    
    if (selectedYear !== registeredYear || month !== registeredMonth) {
      return false;
    }
    
    return registeredDay >= 25 && registeredDay <= 31;
  };
  
  const stats = {
    total: regularMembers.length,
    paidThisMonth: regularMembers.filter(m => {
      const paymentStatus = m.payments[currentMonthStr];
      const isPaid = paymentStatus !== null && paymentStatus !== 'dormant';
      const isDormant = paymentStatus === 'dormant';
      const isBeforeRegistration = getIsBeforeRegistrationForStats(m, currentMonth);
      const isRegistrationMonthExempted = getIsRegistrationMonthExemptedForStats(m, currentMonth);
      
      return isPaid || isDormant || isBeforeRegistration || isRegistrationMonthExempted;
    }).length,
    unpaidThisMonth: regularMembers.filter(m => {
      const paymentStatus = m.payments[currentMonthStr];
      const isPaid = paymentStatus !== null && paymentStatus !== 'dormant';
      const isDormant = paymentStatus === 'dormant';
      const isBeforeRegistration = getIsBeforeRegistrationForStats(m, currentMonth);
      const isRegistrationMonthExempted = getIsRegistrationMonthExemptedForStats(m, currentMonth);
      
      // 실제 미납인 경우만 카운트 (납부했거나, 휴면이거나, 가입 전이거나, 가입월 면제가 아닌 경우)
      return !isPaid && !isDormant && !isBeforeRegistration && !isRegistrationMonthExempted;
    }).length,
    totalAmount: regularMembers.filter(m => {
      const paymentStatus = m.payments[currentMonthStr];
      const isPaid = paymentStatus !== null && paymentStatus !== 'dormant';
      return isPaid;
    }).length * 20000,
  };

  const handleMemberClick = async (member: Member) => {
    setSelectedMember(member);
    setSelectedMonth('1'); // 기본으로 1월 선택
    setIsModalOpen(true);
    
    // 해당 회원의 상세 납부 내역 조회
    try {
      const response = await apiClient.get(`/fees/user/${member.id}/${selectedYear}`);
      if (response.data.success) {
        const details = response.data.data;
        const monthData: { [month: string]: PaymentDetail } = {};
        
        // 모든 월 초기화
        months.forEach(month => {
          monthData[month] = { amount: '', paymentDate: '', memo: '', payment_no: undefined };
        });
        
        // 조회한 상세 내역으로 채우기
        details.forEach((detail: any) => {
          const month = detail.month.toString();
          monthData[month] = {
            amount: detail.amount ? detail.amount.toString() : '',
            paymentDate: detail.date || '',
            memo: detail.memo || '',
            payment_no: detail.payment_no
          };
        });
        
        setEditingMonth(monthData);
      } else {
        // 조회 실패 시 기본값으로 초기화
        const monthData: { [month: string]: PaymentDetail } = {};
        months.forEach(month => {
          const payment = member.payments[month];
          if (isPaymentDetail(payment)) {
            monthData[month] = { ...payment, payment_no: undefined };
          } else {
            monthData[month] = { amount: '', paymentDate: '', memo: '', payment_no: undefined };
          }
        });
        setEditingMonth(monthData);
      }
    } catch (error) {
      console.error('회비 상세 내역 조회 실패:', error);
      // 조회 실패 시 기본값으로 초기화
      const monthData: { [month: string]: PaymentDetail } = {};
      months.forEach(month => {
        const payment = member.payments[month];
        if (isPaymentDetail(payment)) {
          monthData[month] = { ...payment };
        } else {
          monthData[month] = { amount: '', paymentDate: '', memo: '' };
        }
      });
      setEditingMonth(monthData);
    }
  };

  const handleMonthInputChange = (month: string, field: keyof PaymentDetail, value: string) => {
    setEditingMonth(prev => ({
      ...prev,
      [month]: {
        ...prev[month],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedMember) return;

    try {
      // 선택된 월의 납부 정보만 저장
      const monthData = editingMonth[selectedMonth];
      if (!monthData) {
        window.alert('월 정보를 찾을 수 없습니다');
        return;
      }
      
      const amountStr = (monthData.amount || '').trim();
      const paymentDateStr = (monthData.paymentDate || '').trim();
      const memoStr = (monthData.memo || '').trim();
      const hasPaymentData = amountStr || paymentDateStr;
      const hasExistingPayment = monthData.payment_no !== undefined;
      
      // 기존 납부 정보가 있고, amount와 paymentDate가 모두 비어있으면 삭제
      if (hasExistingPayment && !hasPaymentData) {
        if (window.confirm('납부 정보를 삭제하시겠습니까?')) {
          const response = await apiClient.delete(`/fees/payment/${monthData.payment_no}`);
          if (response.data.success) {
            window.alert('회비 정보가 삭제되었습니다');
            fetchFeeStatus(); // 목록 새로고침
            setIsModalOpen(false);
            setSelectedMember(null);
          }
        }
        return;
      }
      
      if (hasPaymentData) {
        // 납부 정보 등록/수정 (백엔드의 ON CONFLICT로 UPSERT 처리)
        const response = await apiClient.post('/fees/payment', {
          user_no: selectedMember.id,
          payment_year: selectedYear,
          payment_month: parseInt(selectedMonth),
          payment_amount: amountStr ? parseInt(amountStr) : 20000,
          payment_date: paymentDateStr || null,
          memo: memoStr || null
        });
        
        if (response.data.success) {
          window.alert('회비 정보가 저장되었습니다');
          fetchFeeStatus(); // 목록 새로고침
          setIsModalOpen(false);
          setSelectedMember(null);
        }
      } else {
        // amount와 paymentDate가 모두 비어있고, 기존 납부 정보도 없으면 아무것도 하지 않음
        setIsModalOpen(false);
        setSelectedMember(null);
      }
    } catch (error: any) {
      console.error('회비 저장 실패:', error);
      window.alert(error.response?.data?.detail || '회비 저장에 실패했습니다');
    }
  };

  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/fees/excel-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const result = response.data.data;
        
        // 업로드 번호 저장
        setCurrentUploadNo(result.upload_no);
        
        // 업로드 요약 조회
        await fetchUploadSummary(result.upload_no);
        
        window.alert(`엑셀 파일이 파싱되었습니다.\n총 ${result.saved_count}건이 저장되었습니다.`);
      }
    } catch (error: any) {
      console.error('엑셀 업로드 실패:', error);
      window.alert(error.response?.data?.detail || '엑셀 업로드에 실패했습니다');
    } finally {
      // 파일 input 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <DashboardLayout title="회비 관리">
      <FeesContainer>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <Title>회비 관리</Title>
            <YearSelect
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {Array.from({ length: 10 }, (_, i) => currentYear - i).map(year => (
                <option key={year} value={year}>
                  {year}년
                </option>
              ))}
            </YearSelect>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant={viewMode === 'normal' ? 'primary' : 'outline'}
                size="small"
                onClick={() => setViewMode('normal')}
              >
                일반
              </Button>
              <Button
                variant={viewMode === 'test' ? 'primary' : 'outline'}
                size="small"
                onClick={() => setViewMode('test')}
              >
                테스트
              </Button>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
          >
            📊 엑셀 일괄 등록
          </Button>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleExcelUpload}
          />
        </Header>
        
        {/* 엑셀 업로드 요약 섹션 */}
        {currentUploadNo && uploadSummary && (
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '16px', 
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            marginBottom: '2rem'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
                엑셀 업로드: {uploadSummary.upload_info.filename}
              </h3>
              <Button 
                variant="outline" 
                size="small"
                onClick={() => {
                  setCurrentUploadNo(null);
                  setUploadSummary(null);
                  setSelectedViewMonth(null);
                  setMonthDetails([]);
                }}
              >
                닫기
              </Button>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '0.25rem' }}>전체 행</div>
                <div style={{ fontSize: '1.125rem', fontWeight: 700 }}>{uploadSummary.upload_info.total_rows}건</div>
              </div>
              <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '0.25rem' }}>등록 완료</div>
                <div style={{ fontSize: '1.125rem', fontWeight: 700 }}>{uploadSummary.upload_info.registered_count}건</div>
              </div>
            </div>
            
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>월별 입금 내역</h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                gap: '0.75rem'
              }}>
                {uploadSummary.monthly_summary.map((month) => (
                  <div
                    key={`${month.year}-${month.month}`}
                    onClick={() => handleMonthSelect(month.year, month.month)}
                    style={{
                      padding: '1rem',
                      border: '2px solid #dee2e6',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      background: month.unmatched_count > 0 ? '#fff3cd' : 'white',
                      borderColor: month.unmatched_count > 0 ? '#ffc107' : '#dee2e6'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#000000';
                      e.currentTarget.style.background = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = month.unmatched_count > 0 ? '#ffc107' : '#dee2e6';
                      e.currentTarget.style.background = month.unmatched_count > 0 ? '#fff3cd' : 'white';
                    }}
                  >
                    <div style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                      {month.year}년 {month.month}월
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                      전체: {month.total_count}건
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#0f5132', marginBottom: '0.25rem' }}>
                      매칭: {month.matched_count}건
                    </div>
                    {month.unmatched_count > 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#dc3545', fontWeight: 600 }}>
                        미매칭: {month.unmatched_count}건
                      </div>
                    )}
                    {month.registered_count > 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#0d6efd', marginTop: '0.25rem' }}>
                        등록: {month.registered_count}건
                      </div>
                    )}
                    {(month.already_registered_count || 0) > 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                        이미등록: {month.already_registered_count}건
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        <StatsRow>
          <StatCard>
            <StatLabel>전체</StatLabel>
            <StatValue>{stats.total}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>납부</StatLabel>
            <StatValue>{stats.paidThisMonth}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>미납</StatLabel>
            <StatValue>{stats.unpaidThisMonth}명</StatValue>
          </StatCard>
          {/* <StatCard>
            <StatLabel>수입</StatLabel>
            <StatValue>{stats.totalAmount.toLocaleString()}원</StatValue>
          </StatCard> */}
        </StatsRow>
        
        <SearchSection>
          <SearchRow>
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="이름으로 검색"
            />
            <Button 
              variant="primary"
              onClick={() => {}}
            >
              검색
            </Button>
          </SearchRow>
          
          <FilterRow>
            <FilterButton 
              active={filterStatus === '전체'}
              onClick={() => setFilterStatus('전체')}
            >
              전체
            </FilterButton>
            <FilterButton 
              active={filterStatus === '납부'}
              onClick={() => setFilterStatus('납부')}
            >
              납부
            </FilterButton>
            <FilterButton 
              active={filterStatus === '미납'}
              onClick={() => setFilterStatus('미납')}
            >
              미납
            </FilterButton>
          </FilterRow>
        </SearchSection>
        
        <FeesList>
          {viewMode === 'test' && (
            <div style={{ 
              padding: '1rem', 
              background: '#fff3cd', 
              borderRadius: '8px', 
              marginBottom: '1rem',
              border: '1px solid #ffc107'
            }}>
              <strong>테스트 모드:</strong> 2025년 7월 31일 이후 가입자 중 회비 memo에 "가입비"가 포함된 회원 또는 유니폼 v1이 없는 회원 목록을 표시합니다.
            </div>
          )}
          {loading ? (
            <EmptyState>로딩 중...</EmptyState>
          ) : filteredMembers.length > 0 ? (
            <TableContainer>
              <Legend>
                <LegendItem>
                  <PaymentIcon paid={true}>✓</PaymentIcon>
                  <span>납부</span>
                </LegendItem>
                <LegendItem>
                  <PaymentIcon paid={false}>✕</PaymentIcon>
                  <span>미납</span>
                </LegendItem>
                <LegendItem>
                  <PaymentIcon beforeRegistration={true}>-</PaymentIcon>
                  <span>가입전</span>
                </LegendItem>
                <LegendItem>
                  <PaymentIcon exempted={true}>가</PaymentIcon>
                  <span>가입월면제</span>
                </LegendItem>
                <LegendItem>
                  <PaymentIcon dormant={true}>휴</PaymentIcon>
                  <span>휴면</span>
                </LegendItem>
              </Legend>
              <Table>
                <Thead>
                  <tr>
                    <Th style={{ textAlign: 'left', paddingLeft: '0.5rem', maxWidth: '120px' }}>이름</Th>
                    {viewMode === 'test' && (
                      <Th style={{ textAlign: 'center', minWidth: '80px' }}>가입비</Th>
                    )}
                    {months.map(month => (
                      <Th key={month}>{month}</Th>
                    ))}
                  </tr>
                </Thead>
                <Tbody>
                  {filteredMembers.map(member => {
                    // 가입일 기준으로 가입 전 달 계산
                    const getIsBeforeRegistration = (month: number): boolean => {
                      if (!member.registeredDate) return false;
                      const registeredDate = new Date(member.registeredDate);
                      const registeredYear = registeredDate.getFullYear();
                      const registeredMonth = registeredDate.getMonth() + 1; // 1-12
                      
                      // 현재 연도와 가입 연도가 다르면 가입 연도 기준으로 판단
                      if (selectedYear !== registeredYear) {
                        return selectedYear < registeredYear || (selectedYear === registeredYear && month < registeredMonth);
                      }
                      
                      // 같은 연도면 가입 월 이전 달은 가입 전
                      return month < registeredMonth;
                    };

                    // 가입월 회비 면제 여부 확인 (가입일이 25일~31일 사이)
                    const getIsRegistrationMonthExempted = (month: number): boolean => {
                      if (!member.registeredDate) return false;
                      const registeredDate = new Date(member.registeredDate);
                      const registeredYear = registeredDate.getFullYear();
                      const registeredMonth = registeredDate.getMonth() + 1; // 1-12
                      const registeredDay = registeredDate.getDate(); // 1-31
                      
                      // 가입월이 아니면 면제 아님
                      if (selectedYear !== registeredYear || month !== registeredMonth) {
                        return false;
                      }
                      
                      // 가입일이 25일~31일 사이면 면제
                      return registeredDay >= 25 && registeredDay <= 31;
                    };

                    return (
                      <Tr key={member.id}>
                        <NameCell onClick={() => handleMemberClick(member)}>
                          {member.name}
                        </NameCell>
                        {viewMode === 'test' && (
                          <Td style={{ textAlign: 'center', fontWeight: 600, color: '#0f5132' }}>
                            {member.joiningFeeAmount ? `${member.joiningFeeAmount.toLocaleString()}원` : '-'}
                          </Td>
                        )}
                        {months.map(month => {
                          const monthNum = parseInt(month);
                          const isBeforeRegistration = getIsBeforeRegistration(monthNum);
                          const isRegistrationMonthExempted = getIsRegistrationMonthExempted(monthNum);
                          const paymentStatus = member.payments[month];
                          const isDormant = paymentStatus === 'dormant';
                          const isPaid = paymentStatus !== null && paymentStatus !== 'dormant';
                          
                          // 미래 월 확인 (현재 연도와 월 기준)
                          const now = new Date();
                          const nowYear = now.getFullYear();
                          const nowMonth = now.getMonth() + 1; // 1-12
                          const isFutureMonth = (selectedYear > nowYear) || 
                                                (selectedYear === nowYear && monthNum > nowMonth);
                          
                          return (
                            <Td key={month}>
                              {isBeforeRegistration ? (
                                <PaymentIcon beforeRegistration={true}>-</PaymentIcon>
                              ) : isRegistrationMonthExempted ? (
                                <PaymentIcon exempted={true}>가</PaymentIcon>
                              ) : isDormant ? (
                                <PaymentIcon dormant={true}>휴</PaymentIcon>
                              ) : isFutureMonth ? (
                                // 미래 월은 빈 상태
                                <span></span>
                              ) : (
                                <PaymentIcon 
                                  paid={isPaid}
                                  onClick={(e) => {
                                    if (!isPaid) {
                                      e.stopPropagation();
                                      
                                      // 회원 전화번호 확인
                                      const memberPhone = memberPhoneMap[member.id];
                                      
                                      if (!memberPhone) {
                                        window.alert('회원의 전화번호가 등록되어 있지 않습니다.');
                                        return;
                                      }
                                      
                                      // 관리자 이름 및 직급
                                      const approverName = user?.name || '관리자';
                                      const approverRole = user?.admin_role || null;
                                      const approverTitle = approverRole ? approverRole : approverName;
                                      
                                      // SMS 메시지 생성
                                      const smsMessage = `안녕하세요 H_B ${approverTitle} 입니다.

${selectedYear}년 ${monthNum}월 회비가 아직 납부되지 않았습니다.

계좌번호  

79423097529 카카오뱅크 추도빈

감사합니다`;
                                      
                                      // 전화번호에서 하이픈 제거
                                      const phoneNumber = memberPhone.replace(/-/g, '');
                                      
                                      // SMS 링크 생성
                                      const smsLink = `sms:${phoneNumber}?body=${encodeURIComponent(smsMessage)}`;
                                      
                                      // SMS 링크 열기
                                      window.location.href = smsLink;
                                    }
                                  }}
                                  style={{ cursor: isPaid ? 'default' : 'pointer' }}
                                >
                                  {isPaid ? '✓' : '✕'}
                                </PaymentIcon>
                              )}
                            </Td>
                          );
                        })}
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyState>검색 결과가 없습니다.</EmptyState>
          )}
        </FeesList>
        
        {/* 회비 상세 입력 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          onClick={() => setIsModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedMember?.name} - 회비 입력</ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            <MonthSelector>
              {months.map(month => {
                const isPaid = editingMonth[month]?.amount || editingMonth[month]?.paymentDate;
                return (
                  <MonthButton
                    key={month}
                    selected={selectedMonth === month}
                    paid={!!isPaid}
                    onClick={() => setSelectedMonth(month)}
                  >
                    {month}
                  </MonthButton>
                );
              })}
            </MonthSelector>
            
            <MonthFormContainer>
              <MonthFormTitle>
                <span>{selectedMonth}월</span>
                <PaymentIcon paid={!!(editingMonth[selectedMonth]?.amount || editingMonth[selectedMonth]?.paymentDate)}>
                  {(editingMonth[selectedMonth]?.amount || editingMonth[selectedMonth]?.paymentDate) ? '✓' : '✕'}
                </PaymentIcon>
              </MonthFormTitle>
              
              <MonthForm>
                <Input
                  label="입금액"
                  type="text"
                  value={editingMonth[selectedMonth]?.amount || ''}
                  onChange={(e) => handleMonthInputChange(selectedMonth, 'amount', e.target.value)}
                  placeholder="20000"
                />
                
                <Input
                  label="납부일"
                  type="date"
                  value={editingMonth[selectedMonth]?.paymentDate || ''}
                  onChange={(e) => handleMonthInputChange(selectedMonth, 'paymentDate', e.target.value)}
                />
                
                <Input
                  label="메모"
                  type="text"
                  value={editingMonth[selectedMonth]?.memo || ''}
                  onChange={(e) => handleMonthInputChange(selectedMonth, 'memo', e.target.value)}
                  placeholder="예: 연납, 분기납 등"
                />
              </MonthForm>
            </MonthFormContainer>
            
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
              <Button
                variant="outline"
                size="large"
                fullWidth
                onClick={() => setIsModalOpen(false)}
              >
                취소
              </Button>
              <Button
                variant="primary"
                size="large"
                fullWidth
                onClick={handleSave}
              >
                저장
              </Button>
            </div>
          </ModalContent>
        </ModalOverlay>
        
        {/* 엑셀 월별 매핑 모달 */}
        <ModalOverlay 
          isOpen={isExcelMappingModalOpen}
          onClick={() => setIsExcelMappingModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
            <ModalHeader>
              <ModalTitle>
                {selectedYear}년 {selectedViewMonth}월 입금 내역
              </ModalTitle>
              <CloseButton onClick={() => setIsExcelMappingModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            {monthDetailsLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>로딩 중...</div>
            ) : (
              <>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                    전체 {monthDetails.length}건
                    {' / '}
                    매칭완료: <span style={{ color: '#0f5132', fontWeight: 600 }}>
                      {monthDetails.filter(d => d.status === '매칭완료').length}건
                    </span>
                    {' / '}
                    미매칭: <span style={{ color: '#dc3545', fontWeight: 600 }}>
                      {monthDetails.filter(d => d.status === '미매칭').length}건
                    </span>
                    {' / '}
                    이미등록: <span style={{ color: '#6c757d', fontWeight: 600 }}>
                      {monthDetails.filter(d => d.status === '이미등록').length}건
                    </span>
                  </div>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={async () => {
                      const matchedDetails = monthDetails
                        .filter(d => d.status === '매칭완료');
                      if (matchedDetails.length > 0) {
                        // 전체 등록은 확인 없이 바로 진행
                        for (const detail of matchedDetails) {
                          await handleRegisterPayments(detail, true); // skipConfirm = true
                        }
                        window.alert(`총 ${matchedDetails.length}건의 회비가 등록되었습니다.`);
                        // 목록 새로고침
                        if (currentUploadNo && selectedViewMonth) {
                          fetchMonthDetails(currentUploadNo, selectedYear, selectedViewMonth);
                          fetchUploadSummary(currentUploadNo);
                        }
                        fetchFeeStatus();
                      } else {
                        window.alert('등록할 항목이 없습니다. 먼저 매핑을 완료해주세요.');
                      }
                    }}
                  >
                    매칭완료 항목 등록
                  </Button>
                </div>
                
                <div style={{ 
                  maxHeight: '60vh', 
                  overflowY: 'auto', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '8px'
                }}>
                  <Table style={{ minWidth: '100%' }}>
                    <Thead>
                      <Tr>
                        <Th>행번호</Th>
                        <Th>거래일시</Th>
                        <Th>금액</Th>
                        <Th>내용</Th>
                        <Th>매칭 회원</Th>
                        <Th>상태</Th>
                        <Th>메모</Th>
                        <Th>액션</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {monthDetails.map((detail) => (
                        <Tr key={detail.detail_no} style={{
                          background: detail.status === '미매칭' ? '#fff3cd' : 
                                     detail.status === '등록완료' ? '#d1e7dd' :
                                     detail.status === '이미등록' ? '#e9ecef' : 'white',
                          borderLeft: detail.is_grouped ? '4px solid #0d6efd' : 'none'
                        }}>
                          <Td>
                            {detail.is_grouped ? (
                              <span style={{ fontSize: '0.75rem', color: '#0d6efd', fontWeight: 600 }}>
                                {detail.row_numbers?.join(', ')} ({detail.count}건)
                              </span>
                            ) : (
                              detail.row_number
                            )}
                          </Td>
                          <Td style={{ fontSize: '0.75rem' }}>
                            {detail.is_grouped ? (
                              <div>
                                {detail.transaction_dates?.slice(0, 2).map((date, idx) => (
                                  <div key={idx}>{new Date(date).toLocaleDateString('ko-KR')}</div>
                                ))}
                                {detail.transaction_dates && detail.transaction_dates.length > 2 && (
                                  <div style={{ color: '#6c757d', fontSize: '0.7rem' }}>
                                    외 {detail.transaction_dates.length - 2}건
                                  </div>
                                )}
                              </div>
                            ) : (
                              detail.transaction_date ? new Date(detail.transaction_date).toLocaleDateString('ko-KR') : '-'
                            )}
                          </Td>
                          <Td style={{ textAlign: 'right', fontWeight: 600 }}>
                            {detail.is_grouped ? (
                              <div>
                                <div style={{ color: '#0d6efd' }}>
                                  합계: {detail.amount.toLocaleString()}원
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                                  {detail.amounts?.map(a => a.toLocaleString()).join(' + ')}
                                </div>
                              </div>
                            ) : (
                              `${detail.amount ? detail.amount.toLocaleString() : '-'}원`
                            )}
                          </Td>
                          <Td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {detail.is_grouped ? (
                              <span title={detail.contents?.join(', ')}>
                                {detail.contents?.slice(0, 2).join(', ')}
                                {detail.contents && detail.contents.length > 2 && '...'}
                              </span>
                            ) : (
                              detail.content || '-'
                            )}
                          </Td>
                          <Td>
                            {detail.status === '미매칭' ? (
                              <select
                                style={{
                                  padding: '0.5rem',
                                  borderRadius: '4px',
                                  border: '1px solid #dee2e6',
                                  fontSize: '0.875rem',
                                  width: '100%',
                                  minWidth: '120px'
                                }}
                                value={detail.matched_user_no || ''}
                                onChange={(e) => {
                                  const userId = parseInt(e.target.value);
                                  if (userId) {
                                    handleMappingUpdate(detail.detail_no, userId);
                                  }
                                }}
                              >
                                <option value="">회원 선택</option>
                                {allMembers.map(member => (
                                  <option key={member.id} value={member.id}>
                                    {member.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span style={{ fontWeight: 600 }}>
                                {detail.matched_name || '-'}
                              </span>
                            )}
                          </Td>
                          <Td>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              background: detail.status === '매칭완료' ? '#d1e7dd' :
                                         detail.status === '등록완료' ? '#cfe2ff' :
                                         detail.status === '이미등록' ? '#e9ecef' :
                                         '#fff3cd',
                              color: detail.status === '매칭완료' ? '#0f5132' :
                                     detail.status === '등록완료' ? '#084298' :
                                     detail.status === '이미등록' ? '#495057' :
                                     '#856404'
                            }}>
                              {detail.status}
                            </span>
                          </Td>
                          <Td>
                            {detail.status === '매칭완료' && (
                              <input
                                type="text"
                                placeholder="메모 입력"
                                value={paymentMemos[detail.detail_no] || ''}
                                onChange={(e) => {
                                  setPaymentMemos(prev => ({
                                    ...prev,
                                    [detail.detail_no]: e.target.value
                                  }));
                                }}
                                style={{
                                  padding: '0.375rem 0.5rem',
                                  borderRadius: '4px',
                                  border: '1px solid #dee2e6',
                                  fontSize: '0.875rem',
                                  width: '100%',
                                  minWidth: '120px'
                                }}
                              />
                            )}
                          </Td>
                          <Td>
                            {detail.status === '매칭완료' && (
                              <Button
                                variant="primary"
                                size="small"
                                onClick={() => handleRegisterPayments(detail)}
                              >
                                등록
                              </Button>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
                
                <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outline"
                    size="large"
                    onClick={() => setIsExcelMappingModalOpen(false)}
                  >
                    닫기
                  </Button>
                </div>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
        
        {/* 회비 등록 모달 (다중 월 선택) */}
        <ModalOverlay 
          isOpen={isRegisterModalOpen}
          onClick={handleCloseRegisterModal}
        >
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <ModalHeader>
              <ModalTitle>회비 등록 - 여러 달 선택</ModalTitle>
              <CloseButton onClick={handleCloseRegisterModal}>×</CloseButton>
            </ModalHeader>
            
            {selectedDetailForRegister && (
              <>
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <strong>회원:</strong> {selectedDetailForRegister.matched_name}
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <strong>입금액:</strong> {selectedDetailForRegister.amount.toLocaleString()}원
                  </div>
                  {selectedDetailForRegister.registered_date && (
                    <div style={{ marginBottom: '0.75rem', color: '#6c757d', fontSize: '0.875rem' }}>
                      <strong>가입일:</strong> {selectedDetailForRegister.registered_date}
                      {selectedDetailForRegister.registered_year && selectedDetailForRegister.registered_month && (
                        <span> ({selectedDetailForRegister.registered_year}년 {selectedDetailForRegister.registered_month}월)</span>
                      )}
                    </div>
                  )}
                  <div style={{ marginBottom: '0.75rem', color: '#6c757d', fontSize: '0.875rem' }}>
                    <strong>입금 월:</strong> {selectedDetailForRegister.payment_year}년 {selectedDetailForRegister.payment_month}월
                  </div>
                </div>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ marginBottom: '0.75rem', fontWeight: 600 }}>
                    등록할 달을 선택하세요 (각 달마다 20,000원씩 등록됩니다):
                  </div>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(4, 1fr)', 
                    gap: '0.75rem',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    padding: '1rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    background: '#f8f9fa'
                  }}>
                    {(() => {
                      const paymentMonth = selectedDetailForRegister.payment_month;
                      const paymentYear = selectedDetailForRegister.payment_year;
                      const registeredMonth = selectedDetailForRegister.registered_month;
                      const registeredYear = selectedDetailForRegister.registered_year;
                      const amount = selectedDetailForRegister.amount;
                      const maxMonths = Math.floor(amount / 20000);
                      
                      // 2025년 1월부터 시작 (2024년은 이미 처리 완료)
                      const MIN_YEAR = 2025;
                      const MIN_MONTH = 1;
                      
                      // 시작 범위: 입금 월부터 시작 (입금 월이 기준)
                      let startYear = paymentYear;
                      let startMonth = paymentMonth;
                      
                      // 가입월이 있고, 입금 월보다 나중이면 가입월부터 시작
                      if (registeredYear && registeredMonth) {
                        if (registeredYear > paymentYear || (registeredYear === paymentYear && registeredMonth > paymentMonth)) {
                          startYear = registeredYear;
                          startMonth = registeredMonth;
                        }
                      }
                      
                      // 최소 시작 범위: 2025년 1월 이전은 안됨
                      if (startYear < MIN_YEAR || (startYear === MIN_YEAR && startMonth < MIN_MONTH)) {
                        startYear = MIN_YEAR;
                        startMonth = MIN_MONTH;
                      }
                      
                      // 끝 범위: 입금 월 기준으로 maxMonths 만큼 미래까지 확장
                      let endYear = paymentYear;
                      let endMonth = paymentMonth + (maxMonths - 1);
                      while (endMonth > 12) {
                        endMonth -= 12;
                        endYear += 1;
                      }
                      
                      // 시작 범위: 입금 월 이전 달들도 포함 (이미 등록되지 않은 달만)
                      // 입금 월 이전부터 시작하되, 최소 2025년 1월, 가입월 이후
                      let earliestYear = paymentYear;
                      let earliestMonth = paymentMonth;
                      
                      // 가입월이 있으면 가입월부터
                      if (registeredYear && registeredMonth) {
                        if (registeredYear < paymentYear || (registeredYear === paymentYear && registeredMonth < paymentMonth)) {
                          earliestYear = registeredYear;
                          earliestMonth = registeredMonth;
                        }
                      }
                      
                      // 최소 시작 범위: 2025년 1월 이전은 안됨
                      if (earliestYear < MIN_YEAR || (earliestYear === MIN_YEAR && earliestMonth < MIN_MONTH)) {
                        earliestYear = MIN_YEAR;
                        earliestMonth = MIN_MONTH;
                      }
                      
                      const months: Array<{ year: number; month: number }> = [];
                      
                      // earliest ~ end 범위의 모든 달을 포함
                      let y = earliestYear;
                      let m = earliestMonth;
                      while (true) {
                        // 2025년 1월 이전은 스킵
                        if (y > MIN_YEAR || (y === MIN_YEAR && m >= MIN_MONTH)) {
                          // 가입월 이전도 스킵 (가입월이 2025년 이후인 경우)
                          if (!(registeredYear && registeredMonth && (y < registeredYear || (y === registeredYear && m < registeredMonth)))) {
                            months.push({ year: y, month: m });
                          }
                        }
                        
                        // 종료 검사
                        if (y === endYear && m === endMonth) break;
                        
                        // 다음 달
                        m += 1;
                        if (m === 13) {
                          m = 1;
                          y += 1;
                        }
                      }
                      
                      // maxMonths보다 많으면 앞에서부터 maxMonths개만 노출
                      const limitedMonths = months.slice(0, Math.max(maxMonths, months.length));
                      
                      return limitedMonths.map((m) => {
                        // 이미 등록된 달인지 확인
                        const isAlreadyRegistered = existingPayments.some(ep => ep.year === m.year && ep.month === m.month);
                        
                        return (
                          <label
                            key={`${m.year}-${m.month}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              cursor: isAlreadyRegistered ? 'not-allowed' : 'pointer',
                              background: isAlreadyRegistered ? '#e9ecef' :
                                         selectedMonthsForRegister.some(sm => sm.year === m.year && sm.month === m.month) ? '#e7f3ff' : 'white',
                              border: isAlreadyRegistered ? '1px solid #adb5bd' :
                                     selectedMonthsForRegister.some(sm => sm.year === m.year && sm.month === m.month) ? '2px solid #0d6efd' : '1px solid #dee2e6',
                              opacity: isAlreadyRegistered ? 0.6 : 1
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedMonthsForRegister.some(sm => sm.year === m.year && sm.month === m.month)}
                              disabled={isAlreadyRegistered}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMonthsForRegister([...selectedMonthsForRegister, { year: m.year, month: m.month }]);
                                } else {
                                  setSelectedMonthsForRegister(selectedMonthsForRegister.filter(sm => !(sm.year === m.year && sm.month === m.month)));
                                }
                              }}
                              style={{ marginRight: '0.5rem', cursor: isAlreadyRegistered ? 'not-allowed' : 'pointer' }}
                            />
                            <span style={{ fontSize: '0.875rem', color: isAlreadyRegistered ? '#6c757d' : '#000000' }}>
                              {m.year}년 {m.month}월
                              {isAlreadyRegistered && <span style={{ marginLeft: '0.25rem', fontSize: '0.75rem', color: '#6c757d' }}>(이미등록)</span>}
                            </span>
                          </label>
                        );
                      });
                    })()}
                  </div>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6c757d' }}>
                    선택된 달: {selectedMonthsForRegister.length}개 (총 {selectedMonthsForRegister.length * 20000}원)
                  </div>
                </div>
                
                <ModalActions>
                  <Button
                    variant="primary"
                    size="large"
                    fullWidth
                    onClick={handleConfirmRegister}
                    disabled={selectedMonthsForRegister.length === 0}
                  >
                    등록 ({selectedMonthsForRegister.length}개월)
                  </Button>
                  <Button
                    variant="secondary"
                    size="large"
                    fullWidth
                    onClick={handleCloseRegisterModal}
                  >
                    취소
                  </Button>
                </ModalActions>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
        
        {/* 엑셀 데이터 미리보기 모달 */}
        <ModalOverlay 
          isOpen={isPreviewModalOpen}
          onClick={() => setIsPreviewModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
            <ModalHeader>
              <ModalTitle>엑셀 데이터 미리보기</ModalTitle>
              <CloseButton onClick={() => setIsPreviewModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#6c757d' }}>
              전체 {totalRows}행 중 최대 50행 표시 (B11부터 G열까지)
            </div>
            
            <div style={{ overflowX: 'auto', maxHeight: '60vh', border: '1px solid #dee2e6', borderRadius: '8px' }}>
              <Table style={{ minWidth: '100%' }}>
                <Thead>
                  <Tr>
                    <Th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>거래일시</Th>
                    <Th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>구분</Th>
                    <Th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>거래금액</Th>
                    <Th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>거래후 잔액</Th>
                    <Th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>거래구분</Th>
                    <Th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>내용</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {previewData.map((row, rowIdx) => (
                    <Tr key={rowIdx}>
                      {row.map((cell, colIdx) => (
                        <Td key={colIdx} style={{ 
                          background: rowIdx === 0 ? '#fff3cd' : 'white',
                          fontSize: '0.75rem',
                          padding: '0.5rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxWidth: colIdx === 5 ? '300px' : '150px',
                          textAlign: colIdx === 2 || colIdx === 3 ? 'right' : 'left'
                        }}>
                          {cell || ''}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
            
            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="primary"
                size="large"
                onClick={() => setIsPreviewModalOpen(false)}
              >
                닫기
              </Button>
            </div>
          </ModalContent>
        </ModalOverlay>
      </FeesContainer>
    </DashboardLayout>
  );
};

export default AdminFees;
